/**
 * useActivationLandingState — les quatre sorties de l'arrivée. Aucune donnée, que de la navigation.
 *
 * ⚠️ « menu », pas « menu/home » : ce dernier est un CHEMIN de deep-link, jamais un nom d'écran —
 * React Navigation refusait l'action et le seul bouton de la page ne faisait rien
 * (`ActivationLandingScreen.js:104-111`).
 */
import { useCallback, useMemo } from "react"
import { Linking } from "react-native"
import { useNavigation } from "@react-navigation/native"
import * as amplitude from "@amplitude/analytics-react-native"

import { API_URL } from "../../../../../../data/constants/apiConstants"
import { type ActivationLinkParams } from "./activationLink"

/**
 * Préfixe Amplitude du funnel de reprise — PARTAGÉ avec la bannière de l'accueil
 * (`selfServeRepriseDisplay.js:29`). La métrique se lit toutes surfaces confondues, c'est
 * `surface` qui les distingue ; deux préfixes en feraient une jointure à la main.
 */
const REPRISE_TRACKING_SOURCE = "parent_reprise.self_serve"

export function useActivationLandingState(linkParams: ActivationLinkParams | null, repriseSeason: string | null) {
    const navigation = useNavigation<{
        replace: (name: string, params?: Record<string, unknown>) => void
        navigate: (name: string, params?: Record<string, unknown>) => void
    }>()

    const openSpace = useCallback(() => navigation.replace("menu"), [navigation])

    const openComposer = useCallback(
        () => navigation.navigate("activation/composer", linkParams ?? undefined),
        [navigation, linkParams]
    )

    /**
     * La destination n'est PAS dans la charge : on ouvre la route signée, qui redirige vers le
     * funnel de la saison. C'est ce qui garde l'arrivée sans identité — l'URL du funnel porte
     * `?user=<base64(email)>`, et la laisser dormir dans un JSON préchargé par les scanners
     * d'entreprise rouvrirait le compte que le lien-capacité a fermé.
     */
    const openReprise = useCallback(() => {
        if (!linkParams) return
        amplitude.track(`${REPRISE_TRACKING_SOURCE}.clicked`, {
            surface: "activation_link",
            season: repriseSeason ?? "regular",
        })
        const query = new URLSearchParams(linkParams).toString()
        void Linking.openURL(`${API_URL}/api/v1/activation-link/reprise?${query}`)
    }, [linkParams, repriseSeason])

    return useMemo(() => ({ openSpace, openComposer, openReprise }), [openSpace, openComposer, openReprise])
}
